import { entryHours } from './time.js';

// Every logged hour is exactly one work type, read off its project — the same
// rule as the server's workTypes.js. Paid absence (vacation, sick leave,
// public holiday… of someone paid for it) counts toward total hours and MD
// like work; unpaid absence is shown on its own and never counted. Client work
// share is over work only, never absence.
export const WORK_TYPES = [
  { id: 'client', label: 'Client work' },
  { id: 'internal', label: 'Internal work' },
  { id: 'paid-absence', label: 'Paid absence' },
  { id: 'unpaid-absence', label: 'Unpaid absence' },
];
export const WORK_TYPE_IDS = WORK_TYPES.map((type) => type.id);

// An entry whose project isn't loaded falls back to its own billable flag.
export function workTypeOf(project, entry) {
  if (project?.absence)
    return project.absencePaid === false ? 'unpaid-absence' : 'paid-absence';
  return (project?.billable ?? entry?.billable) ? 'client' : 'internal';
}

export function workTypeLabel(type) {
  return WORK_TYPES.find((item) => item.id === type)?.label || '';
}

// The one shape every total on screen reads: the four work types, work (client
// + internal) and total (work + paid absence).
export function hoursBreakdown({
  client,
  internal,
  paidAbsence,
  unpaidAbsence,
}) {
  const work = client + internal;
  return {
    client,
    internal,
    paidAbsence,
    unpaidAbsence,
    work,
    total: work + paidAbsence,
  };
}

// Entries carry only their projectId, so the absence flags come from the
// bootstrap projects.
export function sumByWorkType(entries, projects) {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const sums = {
    client: 0,
    internal: 0,
    'paid-absence': 0,
    'unpaid-absence': 0,
  };
  for (const entry of entries)
    sums[workTypeOf(byId.get(entry.projectId), entry)] += entryHours(entry);
  return hoursBreakdown({
    client: sums.client,
    internal: sums.internal,
    paidAbsence: sums['paid-absence'],
    unpaidAbsence: sums['unpaid-absence'],
  });
}

// A /reports/data summary (or a per-person row) in the same shape.
export function hoursFromReport(row) {
  return hoursBreakdown({
    client: row.billableHours || 0,
    internal: row.internalHours || 0,
    paidAbsence: row.paidAbsenceHours || 0,
    unpaidAbsence: row.unpaidAbsenceHours || 0,
  });
}

// The report work type filter is one string, shared app-wide and mirrored to
// the URL as `work=`: 'all', or the kept work types comma-joined in
// WORK_TYPE_IDS order. The single choices it used to be still read: billable
// is client work, non-billable is internal work. Anything else reads as null.
const LEGACY_WORK_FILTER = { billable: 'client', 'non-billable': 'internal' };

export function normalizeWorkFilter(value) {
  if (!value || value === 'all') return 'all';
  const ids = String(value)
    .split(',')
    .map((id) => LEGACY_WORK_FILTER[id] || id);
  if (!ids.every((id) => WORK_TYPE_IDS.includes(id))) return null;
  const kept = WORK_TYPE_IDS.filter((id) => ids.includes(id));
  return kept.length === WORK_TYPE_IDS.length ? 'all' : kept.join(',');
}

export function workFilterIds(value) {
  const normalized = normalizeWorkFilter(value) || 'all';
  return normalized === 'all' ? WORK_TYPE_IDS : normalized.split(',');
}

// Toggle one work type; the last one kept stays, since a filter that keeps
// nothing would only ever show an empty report.
export function toggleWorkFilter(value, id) {
  const ids = workFilterIds(value);
  const next = ids.includes(id)
    ? ids.filter((item) => item !== id)
    : [...ids, id];
  return next.length ? normalizeWorkFilter(next.join(',')) : value;
}
